import { FC, useCallback, useEffect, useRef, useState } from "react";
import ConnectedAccount from "../../../models/ConnectedAccount";
import { Box, CircularProgress, Grid, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useListSecurityControlsQuery } from "../../../hooks/useListSecurityControlsQuery";
import useSnackBars from "../../../hooks/useSnackbar";
import StyledTableCell from "../../common/StyledTableCell";
import Loader from "../../common/Loader";
import LoadingButton from "@mui/lab/LoadingButton";
import FTRNonDisruptiveRecommendationsRow from "./FTRNonDisruptiveRecommendationsRow";
import MultiAutomatedRemediationDialog from "../common/MultiAutomatedRemediationDialog";
import useNewrelic from "../../../hooks/useNewrelic";
import { useLazyQuery } from "@apollo/client";
import { GetControlDocument } from "../../../generated";

interface FTRNonDisruptiveProps {
  targetAccountType: string;
  technicalReviewArn: string;
  connectedAccounts: Array<ConnectedAccount>;
  selectedControls: any[]; // Array of controls currently selected in the parent component
  updateSelectedControls: (control: any, isSelected: boolean) => void; // Function to update the selected controls state
  setUnlockSteps: React.Dispatch<
    React.SetStateAction<{
      step2: boolean;
      step3: boolean;
      step4: boolean;
      step5: boolean;
    }>
  >;
	changeRemediateTab: (index: number) => void;
	changeTab: (index: number) => void;
}

type RowQuestion = {
  controlname: string;
  controldescription: string;
  compliancestatus: string;
	remediatestatus: string;
	remediationUrl: string;
	intrusive: string;
	controlshlink: string;
};

/**
 * All Failed Non Disruptive Recommendations of FTR and CIS 1.4 selected
 * @param targetAccountType returns if account is AUTOMATED or READ_ONLY
 * @param technicalReviewArn technical reveiew arn that was selected when creating the framework
 * @param connectedAccounts state of connectedAccounts information
 * @param selectedControls state to keep track of controls that are selected on this page
 * @param updateSelectedControls called when a control is selected to be remediated from this page
 * @param setUnlockSteps which step to unlock and lock in the tabs
 * @param changeRemediateTab tab to switch to cloudwatch remediate page
 * @param changeTab tab to switch to after this page is completed
 * @returns all Failed Non Disruptive Recommendations of FTR and CIS 1.4 selected
 */
const FTRNonDisruptiveRecommendations: FC<FTRNonDisruptiveProps> = ({
  targetAccountType,
  technicalReviewArn,
  connectedAccounts,
  selectedControls,
  updateSelectedControls,
	setUnlockSteps,
	changeRemediateTab,
	changeTab,
}: FTRNonDisruptiveProps) => {
	const { setAlerts } = useSnackBars();
	const { noticeError } = useNewrelic();
	const [rows, setRows] = useState<Array<RowQuestion>>([]);
	const [rowSelection, setRowSelection] = useState([]);
	const [failedRows, setFailedRows] = useState<string[]>([]);
	const [switchBehavior, setSwitchBehavior] = useState("Off");
	const [open, setOpen] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [activeSpinner, setActiveSpinner] = useState(false);
	const [noRemediation, setNoRemediation] = useState(true);
	const [accountIdRef, setAccountIdRef] = useState<string>("");
	const [failedFindingRows, setFailedFindingRows] = useState<Set<string>>(new Set());
	const scrollRef = useRef<null | HTMLDivElement>(null);

	const { controls, loading, updateData } = useListSecurityControlsQuery({
		arn: technicalReviewArn,
		connectedAccounts,
	});

	const [ GetControl ] = useLazyQuery(GetControlDocument);

	//Set switch to All to remediate all the controls instead of selecting individually
	const handleSwitch = useCallback(() => {
		setSwitchBehavior((prev) => (prev === "Off" ? "All" : "Off")); // Toggle between "All" and "Independent"
	}, []);

	//Handle that unlocks step 3
	const handleUnlockSteps = useCallback(() => {
		setUnlockSteps((prevState) => ({
			...prevState,
			step2: false, // Lock step 2
			step3: true, // Unlock step 3
		}));
	}, []);

	//Open Multi Automted Remediation Modal
	const handleClickOpen = useCallback(() => {
		setOpen(true);
		setActiveSpinner(true);
	}, []);

	//Close Multi Automted Remediation Modal
	const handleClose = useCallback(() => {
		setOpen(false);
		setDisabled(false);
		setActiveSpinner(false);
	}, []);

	//Multi Automated Remediation Confirm
	const handleConfirm = useCallback((
		status: boolean,
		controls: string[],
		msg: string
	) => {
		if (status) {
			setOpen(false); //Closes the Multi Automated Remediation Modal after its initiated all the remediations
			updateData(controls, "remediatestatus", "ENABLED"); //this is done to ensure that when a milestone is created below it gets the latest controls compliance status
			setAlerts([
				{
					severity: "success",
					msg: "Non-Disruptive Recommendations Successfully Enabled.",
				},
			]);
			//switch to step 3
			changeRemediateTab(1);
		} else if (msg !== "") {
			noticeError(msg, "FTR Non Disruptive Recommendations Error")
			setAlerts([
				{
					severity: "error",
					msg,
				},
			]);
		} else {
			setAlerts([
				{
					severity: "error",
					msg: `Non-Disruptive Recommendations toggling failed for following controls ${controls.join(
						", "
					)}`,
				},
			]);
		}
		setOpen(false);
		setDisabled(false);
	}, [changeRemediateTab, updateData]);

	//If AccountType is Read_Only or no controls are selected to be remediated move to Step 4 when Next is clicked
	//Otherwise open Multi Automated Remediation Dialog
	const handleSubmit = useCallback(() => {
		if (targetAccountType !== "AUTOMATED") {
			handleUnlockSteps();
			changeTab(2);
		} else {
			if (noRemediation) {
				handleUnlockSteps();
				changeTab(2);
			} else {
				setDisabled(true);
				handleClickOpen();
			}
		}
	}, [handleUnlockSteps, handleClickOpen, changeTab, targetAccountType, noRemediation]);

	useEffect(() => {
		const filteredControls = controls.filter((control: any) => {
			const standards = control?.customStandard;
			return (
				Array.isArray(standards) &&
				standards.length > 0 &&
				control?.intrusive === "NON-INTRUSIVE" &&
				control?.compliancestatus === "FAILED" &&
				control?.playbooks === "YES" &&
				(
					standards.includes("CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2") ||
					standards.includes("Foundational Technical Review")
				)
			);
		});
		const rowMap = new Map();
		filteredControls.forEach((control: any) => {
			const key = control.controlname; //AWS Control
			if (!rowMap.has(key)) {
				rowMap.set(key, {
					controlname: control.controlname,
					controldescription: control.controldescription,
					compliancestatus: control.compliancestatus,
					remediatestatus: control.remediatestatus,
					remediationUrl: control.remediationUrl,
					intrusive: control.intrusive,
					controlshlink: control.controlshlink,
				});
			}
		});
		// Convert Map to Array and determine final compliance status
    const data = Array.from(rowMap.values()).map(item => ({
      ...item,
    }))
    .sort((a, b) => a.controlname.localeCompare(b.controlname)); // Sort by frameworkControl
    setRows(data);		
	}, [controls, loading]);
	
	useEffect(() => {
		const fetchAllFailedFindings = async () => {
			const failedSet = new Set<string>();
			await Promise.all(
				rows.map(async (row) => {
					try {
						const { data } = await GetControl({
							variables: {
								arn: technicalReviewArn,
								controlname: row.controlname,
								connectedAccounts,
							},
							fetchPolicy: "network-only", // avoid caching issues
						});

						const findings = data?.getControl?.findings ?? [];
						const hasFailed = findings.some((f: any) => f.Compliance?.Status === "FAILED");

						if (hasFailed) {
							failedSet.add(row.controlname);
						}
					} catch (err) {
						noticeError(err, `FTR Non-Disruptive Recommendations Failed to get findings for ${row.controlname}`)
					}
				})
			);
			setFailedFindingRows(failedSet);
		};

		if (rows.length > 0 && connectedAccounts.length > 0) {
			fetchAllFailedFindings();
		}
	}, [rows, technicalReviewArn, connectedAccounts]);

	useEffect(() => {
		//If Controls are selected to be remediated then setNoRemediation to false 
		//so that Multi Automated Remediation Modal opens
		if (rowSelection.length > 0) {
			setNoRemediation(false);
		} else {
			setNoRemediation(true);
		}
	}, [rowSelection]);

	useEffect(() => {
		if (connectedAccounts.length > 0) {
			setAccountIdRef(connectedAccounts?.[0]?.accountIdRef);
		} else {
			setAccountIdRef(technicalReviewArn.split(":")[4]);
		}
	}, [connectedAccounts]);
	
	return (
		<Grid
			container
			spacing={2}
			alignItems="stretch"
		>
			<Grid item xs md={12}>
				<Typography>
					NB: You will need to answer all questions in the one sitting due to
					the way that the workload is updated. If you would like any
					assistance, please reach out to secure@6pillars.ai
				</Typography>
			</Grid>
			<Grid item xs md={12} sx={{ pt: 2 }}>
				<Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
					<Typography fontWeight="bold" noWrap>
						Select All Recommendations?
					</Typography>
					<Typography fontWeight="bold" noWrap>
						&nbsp;
					</Typography>
					<Switch
						checked={switchBehavior === "All"} // Set checked state based on switchBehavior
						disabled={rows.length === 0 || activeSpinner || targetAccountType !== "AUTOMATED"}
						onChange={handleSwitch} // Handle switch toggling
						color="success"
					/>
				</Box>
			</Grid>
			{disabled ? (
				<MultiAutomatedRemediationDialog
					open={open}
					handleClose={handleClose}
					handleConfirm={handleConfirm}
					rows={rowSelection}
					failedRows={failedRows}
					technicalReviewArn={technicalReviewArn}
					accountRefId={accountIdRef}
				/>
			): null}
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
								<StyledTableCell />
                <StyledTableCell align="center">Control ID</StyledTableCell>
                <StyledTableCell align="center">Description</StyledTableCell>
                <StyledTableCell align="center">Compliance Status</StyledTableCell>
								<StyledTableCell align="center">Select</StyledTableCell>
								<StyledTableCell align="center">Findings</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && (
							<TableRow
								key="FTR-NonDisruptiveRecommendations-loader"
								sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
							>
								<TableCell colSpan={12}>
									<Loader/>
								</TableCell>
							</TableRow>
						)}
						{rows.map((row, index) =>(
							<FTRNonDisruptiveRecommendationsRow
								key={row.controlname}
								index={index}
								technicalReviewArn={technicalReviewArn}
								targetAccountType={targetAccountType}		
								rows={rows}
								row={row}
								rowSelection={rowSelection}
								setRowSelection={setRowSelection}
								selectedControls={selectedControls}
								updateSelectedControls={updateSelectedControls}
								failedRows={failedRows}
								setFailedRows={setFailedRows}
								failedFindingRows={failedFindingRows}
								switchBehavior={switchBehavior}
								connectedAccounts={connectedAccounts}
								activeSpinner={activeSpinner}
							/>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<Grid item xs md={12}>
				<Box ref={scrollRef} sx={{ display: "flex", justifyContent: "flex-end" }}>
					<LoadingButton
						variant="contained"
						color="secondary"
						type="submit"
						disabled={disabled}
						loading={activeSpinner}
						onClick={handleSubmit}
						sx={{
							width: 157,
							height: 40,
							bgcolor: "secondary.main",
							"&:hover": {
								bgcolor: "secondary.main",
								color: "secondary.contrastText",
							},
						}}
						loadingIndicator={
							<CircularProgress color={"secondary"} size={22} />
						}
					>
						Next
					</LoadingButton>
				</Box>
			</Grid>
		</Grid>
	)
}
export default FTRNonDisruptiveRecommendations;